/**
 * Shared list defaults and numeric helpers for Opportunities list and swipe views.
 */

import { normalizeThreadEmail } from "@/pages/DealLensDetail/utils/threadIdentity";
import { propertyHasPhotos } from "@/pages/PropertyList/utils/propertyArtifactFlags";
import { formatAddress } from "@/utils/stringUtils";
import { buildThreadKeyFromDeal } from "@/store/dealCore/dealLensThreadCacheKey";
import { rowDealId } from "@/store/dealCore/deallensIdUtils";
import { getOpportunityOfferStatusPill } from "@/store/dealCore/dealOfferThreadUtils";
import { DEAL_SWIPE_BUCKET, normalizeSwipeBucket } from "@/store/dealCore/dealsConstants";
import { IS_DEV } from "@/utils/env";
import {
  OPPORTUNITIES_REPRESENTATIVE_OVERRIDE,
  OPPORTUNITIES_REPRESENTATIVE_OVERRIDE_ENABLED,
} from "@/utils/opportunitiesRepresentativeOverride";
import { isOpportunityDealAvailable } from "./utils/opportunityListFilterUtils";

/** Default filter payload for DeallensStore.loadList (static; not user-edited in current UI). */
export const DEFAULT_FILTERS = {
  county: "",
  status: "",
};

/**
 * Normalized filters for {@link DeallensStore.loadList} on the Opportunities hub (shared with background prefetch).
 * @param {{ county?: string, status?: string }} [filters]
 * @returns {{ county: null|string, status: null|string, latestPerThread: boolean }}
 */
export function buildOpportunitiesLoadListFilters(filters = DEFAULT_FILTERS) {
  return {
    county: filters.county || null,
    status: filters.status || null,
    latestPerThread: true,
  };
}

/**
 * @typedef {{ viewerUserId?: string | null, viewerEmail?: string | null }} ViewerRepresentativeIdentity
 */

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeRepresentativeToken(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

/**
 * Viewer tokens used to match `deal.representative`.
 * ASSUMPTION: BE contract is email; some rows still store Cognito user id — match both until data is normalized.
 *
 * @param {string | ViewerRepresentativeIdentity | null | undefined} viewerIdentity
 * @returns {Set<string>}
 */
function collectViewerRepresentativeTokens(viewerIdentity) {
  const tokens = new Set();
  if (typeof viewerIdentity === "string") {
    const token = normalizeRepresentativeToken(viewerIdentity);
    if (token) tokens.add(token);
    return tokens;
  }
  const userId = normalizeRepresentativeToken(viewerIdentity?.viewerUserId);
  const email = normalizeRepresentativeToken(viewerIdentity?.viewerEmail);
  if (userId) tokens.add(userId);
  if (email) tokens.add(email);
  return tokens;
}

/**
 * When the override switch is on and override has any token, use it; otherwise use the signed-in identity.
 *
 * @param {string | ViewerRepresentativeIdentity | null | undefined} viewerIdentity
 * @returns {string | ViewerRepresentativeIdentity | null | undefined}
 */
export function resolveRepresentativeMatchIdentity(viewerIdentity) {
  if (!OPPORTUNITIES_REPRESENTATIVE_OVERRIDE_ENABLED) return viewerIdentity;
  const overrideIdentity = {
    viewerUserId: OPPORTUNITIES_REPRESENTATIVE_OVERRIDE.userId,
    viewerEmail: OPPORTUNITIES_REPRESENTATIVE_OVERRIDE.email,
  };
  if (collectViewerRepresentativeTokens(overrideIdentity).size === 0) return viewerIdentity;
  return overrideIdentity;
}

/**
 * True when the deal's property representative matches the signed-in user
 * (or {@link OPPORTUNITIES_REPRESENTATIVE_OVERRIDE} when the override switch is on).
 * Missing `representative` is treated as not a match.
 *
 * @param {object | null | undefined} deal
 * @param {string | ViewerRepresentativeIdentity | null | undefined} viewerIdentity
 * @returns {boolean}
 */
export function isDealRepresentedByViewer(deal, viewerIdentity) {
  const matchIdentity = resolveRepresentativeMatchIdentity(viewerIdentity);
  const representative = normalizeRepresentativeToken(deal?.representative);
  if (!representative) return false;
  const viewerTokens = collectViewerRepresentativeTokens(matchIdentity);
  if (viewerTokens.size === 0) return false;
  return viewerTokens.has(representative);
}

/**
 * Stable partition: non-representative deals first, viewer-representative deals last.
 * Preserves relative order within each bucket (pagination-safe).
 *
 * @param {object[]} deals
 * @param {string | ViewerRepresentativeIdentity | null | undefined} viewerIdentity
 * @returns {object[]}
 */
export function orderDealsWithViewerRepresentativeLast(deals, viewerIdentity) {
  if (!Array.isArray(deals) || deals.length < 2) return deals ?? [];
  const matchIdentity = resolveRepresentativeMatchIdentity(viewerIdentity);
  if (collectViewerRepresentativeTokens(matchIdentity).size === 0) return deals;

  const nonRepresentative = [];
  const representative = [];
  for (const deal of deals) {
    if (isDealRepresentedByViewer(deal, viewerIdentity)) {
      representative.push(deal);
    } else {
      nonRepresentative.push(deal);
    }
  }
  if (representative.length === 0) return deals;
  return [...nonRepresentative, ...representative];
}

/**
 * DEV-only visual marker for viewer-representative deals.
 *
 * @param {object | null | undefined} deal
 * @param {string | ViewerRepresentativeIdentity | null | undefined} viewerIdentity
 * @returns {object}
 */
export function getRepresentativeDevBorderSx(deal, viewerIdentity) {
  if (!IS_DEV) {
    return {};
  }
  if (!isDealRepresentedByViewer(deal, viewerIdentity)) {
    return {};
  }
  return {
    border: "3px solid red",
    boxSizing: "border-box",
  };
}

/**
 * Stable equality for hub list filters (R2: avoid relying on `JSON.stringify` key order).
 * @param {object|null|undefined} a
 * @param {object|null|undefined} b
 * @returns {boolean}
 */
export function areOpportunitiesHubListFiltersEqual(a, b) {
  if (a === b) return true;
  const norm = (x) => ({
    county: x?.county ?? null,
    status: x?.status ?? null,
    latestPerThread: Boolean(x?.latestPerThread),
  });
  const na = norm(a);
  const nb = norm(b);
  return na.county === nb.county && na.status === nb.status && na.latestPerThread === nb.latestPerThread;
}

/**
 * Safely converts a value to a finite number.
 * @param {*} value
 * @returns {number}
 */
export const toNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

/**
 * Calculates total costs from deal calculation metrics.
 * @param {Object} deal
 * @returns {number}
 */
export const getDealTotalCosts = (deal) => {
  const calc = deal?.calculation || {};
  return (
    toNumber(calc.agentClosingCosts) +
    toNumber(calc.buyerClosingCosts) +
    toNumber(calc.sellingClosingCosts) +
    toNumber(calc.profit) +
    toNumber(calc.repairCosts) +
    toNumber(calc.holdingCosts)
  );
};

/**
 * Debug snapshot for one opportunity row when investigating duplicate cards.
 * @param {object} deal
 * @returns {object}
 */
export function buildOpportunityDuplicateDebugSnapshot(deal) {
  const metadata = deal?.metadata && typeof deal.metadata === "object" ? deal.metadata : {};
  const customFields =
    metadata.custom_fields && typeof metadata.custom_fields === "object" ? metadata.custom_fields : {};
  const calculation = deal?.calculation && typeof deal.calculation === "object" ? deal.calculation : {};
  const raw = deal?.raw && typeof deal.raw === "object" ? deal.raw : {};

  return {
    id: rowDealId(deal),
    deallens_id: deal?.deallens_id ?? null,
    thread_id: deal?.thread ?? deal?.thread_id ?? null,
    thread_key: buildThreadKeyFromDeal(deal),
    property_id: deal?.property_id ?? null,
    collection_id: deal?.collection_id ?? null,
    full_address: deal?.full_address ?? "",
    city: deal?.city ?? "",
    state: deal?.state ?? "",
    zipcode: deal?.zipcode ?? "",
    county: deal?.county ?? "",
    hidden_address: Boolean(deal?.hidden_address),
    sender: deal?.sender ?? "",
    receiver: deal?.receiver ?? "",
    sender_role: deal?.sender_role ?? "",
    receiver_role: deal?.receiver_role ?? "",
    homeowner_email: deal?.homeowner_email ?? null,
    representative: deal?.representative ?? null,
    status: deal?.status ?? "",
    swipe_status: deal?.swipe_status ?? "",
    swipe_bucket: normalizeSwipeBucket(deal?.swipe_status),
    active: deal?.active,
    insert_date: deal?.insert_date ?? null,
    last_modified: deal?.last_modified ?? null,
    bed: deal?.bed ?? null,
    bath: deal?.bath ?? null,
    sqft: deal?.sqft ?? null,
    year_built: deal?.year_built ?? null,
    calculation: {
      address: calculation.address ?? null,
      listing_price: calculation.listing_price ?? null,
      final_arv: calculation.final_arv ?? null,
      arv: calculation.arv ?? null,
      mao: calculation.mao ?? null,
      roi: calculation.roi ?? null,
      offer_amount: calculation.offer_amount ?? null,
    },
    metadata: {
      previous_deallens_id: metadata.previous_deallens_id ?? null,
      custom_fields_address:
        customFields.full_address || customFields.address || customFields.property_address || null,
    },
    raw: {
      thread: raw.thread ?? null,
      id: raw.id ?? null,
      deallens_id: raw.deallens_id ?? null,
      property_id: raw.property_id ?? raw.property ?? null,
      collection_id: raw.collection_id ?? null,
    },
    list_card_key: deal?.id ?? null,
  };
}

/**
 * DEV-only: logs `deal.representative` for each visible opportunity row.
 * @param {object[]} deals
 * @param {string | ViewerRepresentativeIdentity | null | undefined} [viewerIdentity]
 */
export function logOpportunityRepresentatives(deals, viewerIdentity) {
  if (!IS_DEV || !Array.isArray(deals) || deals.length === 0) return;

  const matchIdentity = resolveRepresentativeMatchIdentity(viewerIdentity);
  const viewerTokens = [...collectViewerRepresentativeTokens(matchIdentity)];
  const viewerEmail =
    typeof matchIdentity === "object" && matchIdentity != null
      ? matchIdentity.viewerEmail ?? null
      : null;
  const viewerUserId =
    typeof matchIdentity === "object" && matchIdentity != null
      ? matchIdentity.viewerUserId ?? null
      : typeof matchIdentity === "string"
        ? matchIdentity
        : null;

  console.groupCollapsed(`[Opportunities] deal.representative (${deals.length} visible)`);
  for (const deal of deals) {
    const representative = deal?.representative ?? null;
    const representativeStr = String(representative ?? "").trim();
    console.log({
      id: rowDealId(deal),
      representative,
      representativeFormat: representativeStr.includes("@") ? "email" : representativeStr ? "opaque_id" : "missing",
      viewerUserId,
      viewerEmail,
      viewerTokens,
      isViewerRepresentative: isDealRepresentedByViewer(deal, viewerIdentity),
    });
  }
  console.groupEnd();
}

/**
 * Logs opportunities that share the same address (debug aid for duplicate-card regressions).
 * @param {object[]} deals
 */
export function logDuplicateOpportunityAddresses(deals) {
  if (!IS_DEV || !Array.isArray(deals) || deals.length < 2) return;

  /** @type {Map<string, object[]>} */
  const byAddress = new Map();

  for (const deal of deals) {
    const address = String(deal?.full_address ?? "").trim();
    if (!address) continue;
    const key = address.toLowerCase();
    const group = byAddress.get(key);
    if (group) {
      group.push(deal);
    } else {
      byAddress.set(key, [deal]);
    }
  }

  for (const group of byAddress.values()) {
    if (group.length < 2) continue;

    const snapshots = group.map(buildOpportunityDuplicateDebugSnapshot);
    const unique = (values) => [...new Set(values.filter(Boolean))];
    const threadIds = unique(snapshots.map((s) => s.thread_id));
    const propertyIds = unique(snapshots.map((s) => s.property_id));
    const threadKeys = unique(snapshots.map((s) => s.thread_key));
    const collectionIds = unique(snapshots.map((s) => s.collection_id));
    const address = group[0]?.full_address ?? "";

    console.warn("[Opportunities] duplicate address group", {
      address,
      count: group.length,
      thread_ids: threadIds,
      property_ids: propertyIds,
      thread_keys: threadKeys,
      collection_ids: collectionIds,
      same_property_id: propertyIds.length === 1,
      same_thread_key: threadKeys.length === 1,
      same_thread_id: threadIds.length === 1,
      same_collection_id: collectionIds.length === 1,
    });

    console.groupCollapsed(`[Opportunities] duplicate address rows (${group.length}) — ${address}`);
    for (const snapshot of snapshots) {
      console.log(snapshot);
    }
    console.groupEnd();
  }
}

/**
 * Builds OpportunitiesCard props from a deal plus action callbacks.
 * @param {object} deal
 * @param {object} actions
 * @param {boolean} actions.isSwipeInFlight
 * @param {() => void} actions.onViewDetails
 * @param {() => void} actions.onFavoriteToggle
 * @param {() => void} actions.onDismiss
 * @param {{ viewerEmail?: string | null, isInvestor?: boolean, isDistributeRole?: boolean, currentRole?: string | null }} [viewerContext]
 * @returns {object}
 */
export function buildOpportunitiesCardProps(deal, actions, viewerContext = {}) {
  const cityStateFromFields = [deal?.city, deal?.state].filter(Boolean).join(", ");
  const rawFullAddress = (deal?.full_address || "").trim();
  const cityStateFromAddress =
    rawFullAddress && rawFullAddress.includes(",") ? rawFullAddress.split(",").slice(1).join(",").trim() : "";
  const cityState = formatAddress(cityStateFromFields || cityStateFromAddress);
  const swipeBucket = normalizeSwipeBucket(deal?.swipe_status);
  const isFavorite = swipeBucket === DEAL_SWIPE_BUCKET.LIKED;
  const offerStatusPill = getOpportunityOfferStatusPill(deal, viewerContext);
  const isUnavailable = !isOpportunityDealAvailable(deal);

  return {
    address: formatAddress(deal?.full_address || "", Boolean(deal?.hidden_address)),
    cityState,
    beds: toNumber(deal?.bed),
    baths: toNumber(deal?.bath),
    sqft: toNumber(deal?.sqft),
    arv: toNumber(deal?.calculation?.final_arv ?? deal?.calculation?.arv),
    purchase: toNumber(deal?.calculation?.listing_price),
    mao: toNumber(deal?.calculation?.mao),
    roi: toNumber(deal?.calculation?.roi),
    repairs: toNumber(deal?.calculation?.repairCosts ?? deal?.calculation?.repairs),
    mapCounty: deal?.county || "",
    mapZipcode: deal?.zipcode || "",
    propertyId: String(deal?.property_id || deal?.id || ""),
    maskedAddress: Boolean(deal?.hidden_address),
    yearBuilt: deal?.year_built ?? null,
    annualPropertyTax: deal?.annual_property_tax ?? null,
    isFavorite,
    dismissMode: swipeBucket === DEAL_SWIPE_BUCKET.PASSED ? "undo" : "pass",
    isSwipeInFlight: actions.isSwipeInFlight,
    isUnavailable,
    hasPhotos: propertyHasPhotos(deal?.propertyDetails),
    statusBadge: offerStatusPill,
    onViewDetails: isUnavailable ? undefined : actions.onViewDetails,
    onFavoriteToggle: isUnavailable ? undefined : actions.onFavoriteToggle,
    onDismiss: isUnavailable ? undefined : actions.onDismiss,
  };
}
